// The business's logo on bills (docs/plans/bill-logo.md): any picture the owner picks becomes
// a small PNG on white, blank edges trimmed, fitted to 400 × 160, kept as a data URL on
// tenant_settings so every device prints it offline. The server refuses anything else.

/** The longest data URL the server keeps (tenant_settings.logo check): about 48 KB of PNG. */
export const LOGO_MAX = 70_000
export const LOGO_BOX = { width: 400, height: 160 } as const

/** What counts as blank: transparent, or close enough to white. */
function blank(r: number, g: number, b: number, a: number): boolean {
  return a < 16 || (r > 244 && g > 244 && b > 244)
}

/**
 * A logo saved on a solid background (all four corners the same colour) gets that colour
 * turned white, so it sits on the paper rather than in a coloured block. Changes `pixels`;
 * returns whether it did.
 */
export function clearBackground(pixels: Uint8ClampedArray, width: number, height: number): boolean {
  const at = (x: number, y: number) => (y * width + x) * 4
  const corners = [at(0, 0), at(width - 1, 0), at(0, height - 1), at(width - 1, height - 1)]
  const [r, g, b, a] = [
    pixels[corners[0]!]!,
    pixels[corners[0]! + 1]!,
    pixels[corners[0]! + 2]!,
    pixels[corners[0]! + 3]!,
  ]
  if (blank(r, g, b, a)) return false
  const near = (i: number, tolerance: number) =>
    Math.abs(pixels[i]! - r) <= tolerance &&
    Math.abs(pixels[i + 1]! - g) <= tolerance &&
    Math.abs(pixels[i + 2]! - b) <= tolerance &&
    pixels[i + 3]! > 200
  if (!corners.every((i) => near(i, 10))) return false
  for (let i = 0; i < pixels.length; i += 4) {
    if (near(i, 18)) pixels.set([255, 255, 255, 255], i)
  }
  return true
}

/**
 * The smallest box holding every pixel that is not blank, as {x, y, width, height}; null for
 * an all-blank picture. `pixels` is RGBA, row by row.
 */
export function trimBox(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } | null {
  let top = height
  let left = width
  let right = -1
  let bottom = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      if (blank(pixels[i]!, pixels[i + 1]!, pixels[i + 2]!, pixels[i + 3]!)) continue
      if (y < top) top = y
      if (y > bottom) bottom = y
      if (x < left) left = x
      if (x > right) right = x
    }
  }
  if (right < 0) return null
  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 }
}

/** Width and height that fit `w` × `h` inside the box, never larger than the picture. */
export function fitLogo(w: number, h: number, scale = 1): { width: number; height: number } {
  const ratio = Math.min(LOGO_BOX.width / w, LOGO_BOX.height / h, 1) * scale
  return { width: Math.max(1, Math.round(w * ratio)), height: Math.max(1, Math.round(h * ratio)) }
}

export interface PreparedLogo {
  dataUrl: string
  width: number
  height: number
}

/** A chosen file made ready to print. Throws with words for the person when it cannot be. */
export async function prepareLogo(file: File): Promise<PreparedLogo> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new Error('That file is not a picture. Choose a PNG, JPG or WebP.')
  }
  const source = document.createElement('canvas')
  source.width = bitmap.width
  source.height = bitmap.height
  const sg = source.getContext('2d', { willReadFrequently: true })
  if (!sg) throw new Error('This browser cannot read pictures.')
  // On white first: a transparent logo prints on paper.
  sg.fillStyle = '#fff'
  sg.fillRect(0, 0, source.width, source.height)
  sg.drawImage(bitmap, 0, 0)
  bitmap.close()
  const image = sg.getImageData(0, 0, source.width, source.height)
  if (clearBackground(image.data, source.width, source.height)) sg.putImageData(image, 0, 0)
  const box = trimBox(image.data, source.width, source.height)
  if (!box) throw new Error('That picture is blank. Choose your logo.')

  for (let scale = 1; scale > 0.2; scale *= 0.8) {
    const size = fitLogo(box.width, box.height, scale)
    const out = document.createElement('canvas')
    out.width = size.width
    out.height = size.height
    const g = out.getContext('2d')
    if (!g) throw new Error('This browser cannot resize pictures.')
    g.fillStyle = '#fff'
    g.fillRect(0, 0, size.width, size.height)
    g.imageSmoothingQuality = 'high'
    g.drawImage(source, box.x, box.y, box.width, box.height, 0, 0, size.width, size.height)
    const dataUrl = out.toDataURL('image/png')
    if (dataUrl.length <= LOGO_MAX) return { dataUrl, ...size }
  }
  throw new Error('That picture is too detailed to keep small. Try a simpler version of the logo.')
}

/** About how many KB a data URL holds (for the dialog). */
export function dataUrlKb(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
  return Math.max(1, Math.round((base64.length * 3) / 4 / 1024))
}
